import { yupResolver } from '@hookform/resolvers/yup'
import { Checkbox, Button } from '@mymoons/ui-library'
import { decode as base64Decode } from 'base-64'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import nookies from 'nookies'
import { useState, useEffect } from 'react'
import type { KeyboardEvent } from 'react'
import { useForm, Controller } from 'react-hook-form'

import { Input } from '~components/Input'
import type { Country } from '~components/Input'
import countryCodes from '~constants/countryCodes.constant.json'
import { useApp } from '~hooks/useApp'
import { useConfirmPreviousEvaluationMutation } from '~hooks/useConfirmPreviousEvaluationMutation'
import { useCreateEvaluationMutation } from '~hooks/useCreateEvaluationMutation'
import { Evaluation } from '~models/evaluation.model'
import type { Locales } from '~models/locales.model'
import { EvaluationStatus } from '~models/status'
import VIEWS from '~modules/pictures/models/ActiveViews.model'
import { adaptRegistrationFormValuesToService } from '~modules/registration/adapters/registration.adapter'
import { localeToCountryCode } from '~utilities/locales.utilities'
import { getPhoneNumberLength } from '~utilities/locales.utilities'
import { freshpaint } from '~utilities/tracking.utilities'

import { getRegistrationSchema } from '../../models/registration.model'
import type { RegistrationSchema } from '../../models/registration.model'
import * as Styled from './RegistrationForm.styled'

export const RegistrationForm = () => {
  const router = useRouter()
  const { tokenId } = router.query
  const t = useTranslations() // register, common
  const [countryCode, setCountryCode] = useState<Country>()
  const [phoneMaxLength, setPhoneMaxLength] = useState<number>(15)
  const [phoneMinLength, setPhoneMinLength] = useState<number>(6)
  const { setModal, openUnknownErrorModal, redirectToLastCompletedStep } = useApp()

  const { mutate: createEvaluation, isLoading: isCreatingNewEvaluation } =
    useCreateEvaluationMutation()

  const { mutate: confirmPreviousEvaluationExistance, isLoading: isLookigForAPreviousEvaluation } =
    useConfirmPreviousEvaluationMutation()

  useEffect(() => {
    setCountryCode(countryCodes.find((c) => c.id === localeToCountryCode(router.locale as Locales)))
  }, [router.locale])

  useEffect(() => {
    if (countryCode) {
      const locale = countryCode.id as Locales
      const { max, min } = getPhoneNumberLength(locale)
      setPhoneMaxLength(max)
      setPhoneMinLength(min)
    }
  }, [countryCode])

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    watch,
    setValue,
  } = useForm<RegistrationSchema>({
    mode: 'all',
    resolver: yupResolver(getRegistrationSchema(t, countryCode?.id || router.locale)),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      confirmPhone: true,
      confirmPrivacy: false,
    },
  })

  useEffect(() => {
    if (tokenId) {
      const tokenIdDecoded = base64Decode(tokenId as string)
      const tokenData = JSON.parse(tokenIdDecoded)
      setValue('phone', tokenData.phone)
      setCountryCode(countryCodes.find((c) => c.id === localeToCountryCode(tokenData.countryCode)))
      router.replace('/registro', undefined, { shallow: true })
    }
  }, [tokenId, setValue, router])

  const createNewEvaluationAndRedirectToNextStep = (data: RegistrationSchema) => {
    const newEvaluation = adaptRegistrationFormValuesToService({
      ...data,
      countryCode: countryCode!,
    })

    createEvaluation(newEvaluation, {
      onSuccess: (evaluation) => {
        freshpaint()?.track('step_one_registration', {
          crmId: evaluation.dealId,
          email: evaluation.email,
        })

        // Replaces the email saved in the cookies to prevent updating a wrong deal
        nookies.set(null, 'email', evaluation.email, {
          maxAge: 30 * 24 * 60 * 60,
          path: '/',
        })

        router.push(`/fotos/frontal/${VIEWS.instructions}`)
      },
      onError: openUnknownErrorModal,
    })
  }

  const openNotFinishedEvaluationModal = (evaluation: Evaluation) => {
    freshpaint()?.track('prediagnostic_show_not_finished_evaluation_modal', {
      crmId: evaluation.dealId,
    })

    setModal({
      title: t('register.continue.title'),
      description: t('register.continue.description'),
      primaryButtonLabel: t('register.continue.primaryButtonLabel'),
      onPrimaryButtonClick: () => redirectToLastCompletedStep(evaluation),
      secondaryButtonLabel: t('register.continue.secondaryButtonLabel'),
      onSecondaryButtonClick: () => setModal(undefined),
    })
  }

  const openPreviousEvaluationModal = (data: RegistrationSchema) => {
    freshpaint()?.track('prediagnostic_show_previous_evaluation_modal')

    setModal({
      title: t('register.modal.title'),
      description: t('register.modal.description'),
      primaryButtonLabel: t('register.modal.primaryButtonLabel'),
      onPrimaryButtonClick: () => createNewEvaluationAndRedirectToNextStep(data),
      secondaryButtonLabel: t('register.modal.secondaryButtonLabel'),
      onSecondaryButtonClick: () => router.push('/resultados'),
    })
  }

  const onFormSubmit = (data: RegistrationSchema) => {
    return confirmPreviousEvaluationExistance(
      { email: data.email },
      {
        onSuccess: (evaluation) => {
          const hasAnOnGoingEvaluation = evaluation.state === EvaluationStatus.STARTED
          if (hasAnOnGoingEvaluation) return openNotFinishedEvaluationModal(evaluation)

          const hasAPreviousEvaluation = [
            EvaluationStatus.EVALUATED,
            EvaluationStatus.INVALID_DATA,
            EvaluationStatus.WAITING_REVIEW,
          ].includes(evaluation.state)
          if (hasAPreviousEvaluation) return openPreviousEvaluationModal(data)

          createNewEvaluationAndRedirectToNextStep(data)
        },
      },
    )
  }

  return (
    <Styled.Container>
      <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
        <Styled.InputContainer>
          <Controller
            name="firstName"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input
                id={name}
                name={name}
                fullWidth
                error={errors?.firstName?.message}
                label={t('register.firstName').concat('*')}
                placeholder={t('register.firstName')}
                variant="outline"
                value={value}
                onChange={onChange}
                maxLength={60}
                pattern="(.|\n)*?"
                style={{ width: '100%' }}
              />
            )}
          />
        </Styled.InputContainer>
        <Styled.InputContainer>
          <Controller
            name="lastName"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input
                id={name}
                name={name}
                fullWidth
                error={errors?.lastName?.message}
                label={t('register.lastName').concat('*')}
                placeholder={t('register.lastName')}
                variant="outline"
                value={value}
                onChange={onChange}
                maxLength={60}
                pattern="(.|\n)*?"
                style={{ width: '100%' }}
                type={''}
              />
            )}
          />
        </Styled.InputContainer>
        <Styled.InputContainer>
          <Controller
            name="email"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input
                id={name}
                name={name}
                type="email"
                fullWidth
                error={errors?.email?.message}
                label={t('register.email').concat('*')}
                placeholder={t('register.email')}
                variant="outline"
                value={value}
                onChange={onChange}
                maxLength={60}
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,3}$"
                className="input-email"
              />
            )}
          />
        </Styled.InputContainer>
        <Styled.InputContainer>
          <Controller
            name="phone"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input
                id={name}
                testId="phone-input"
                name={name}
                fullWidth
                type="text"
                isCountryCode
                error={errors?.phone?.message}
                label={t('register.phone').concat('*')}
                placeholder=""
                variant="outline"
                value={value}
                onKeyPress={(event: KeyboardEvent<HTMLImageElement>) => {
                  if (event.charCode !== 13 && !/[0-9]/.test(event.key)) {
                    event.preventDefault()
                  }
                }}
                onChange={onChange}
                valueCountryCode={countryCode}
                onChangeCode={(country) => setCountryCode(country)}
                maxLength={phoneMaxLength}
                prefixList={countryCodes}
                pattern="[0-9]*"
              />
            )}
          />
        </Styled.InputContainer>
        <Styled.InputContainer>
          <Controller
            name="confirmPhone"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <>
                <Checkbox
                  disabled={watch('phone').length >= phoneMinLength ? false : true}
                  id={name}
                  name={name}
                  checked={value || false}
                  error={errors?.confirmPhone?.message}
                  onChange={onChange}
                  label={t.rich('register.confirmPhone', {
                    phoneNumber: `${countryCode?.value}${watch('phone')}`,
                    strong: (child) => <strong>{child}</strong>,
                  })}
                />
              </>
            )}
          />
        </Styled.InputContainer>
        <Styled.InputContainer>
          <Controller
            name="confirmPrivacy"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <>
                <Checkbox
                  id={name}
                  name={name}
                  checked={value || false}
                  error={errors?.confirmPrivacy?.message}
                  onChange={onChange}
                  label={
                    <p>
                      {t('register.confirmPrivacy')}
                      <Styled.CheckboxLink
                        href={t('common.legal.privacyAction')}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('common.legal.privacyText')}
                      </Styled.CheckboxLink>
                    </p>
                  }
                />
              </>
            )}
          />
        </Styled.InputContainer>
        <Styled.ButtonContainer>
          <Button
            isButton
            type="submit"
            color="red"
            label={t('register.next')}
            size="medium"
            variant="filled"
            fullWidth
            disabled={!isValid}
            loading={isCreatingNewEvaluation || isLookigForAPreviousEvaluation}
          />
        </Styled.ButtonContainer>
      </form>
    </Styled.Container>
  )
}
